import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Phone, CheckCircle, Star } from "lucide-react";
import { AnimatedSection } from "@/components/ui/animated-section";
import HeroCallbackForm from "@/components/home/HeroCallbackForm";

// Import hero background
import rackingDramatic from "@/assets/projects/racking-aisle-dramatic.webp";
import rackingDramaticMobile from "@/assets/projects/racking-aisle-dramatic-mobile.webp";
import constructionSiteWide from "@/assets/projects/construction-site-wide.jpg";
import designBuildWarehouse from "@/assets/projects/design-build-warehouse.jpg";
import conveyorSystem from "@/assets/projects/conveyor-system.jpg";

// Cinematic montage: full construction mixed with automation. Racking stays
// as the instant-loading first frame; the rest crossfade in after idle.
const montageSlides = [constructionSiteWide, designBuildWarehouse, conveyorSystem];

// Import logos
import amazonLogo from "@/assets/logos/amazon.avif";
import dhlLogo from "@/assets/logos/dhl.avif";
import cocaColaLogo from "@/assets/logos/coca-cola.webp";
import deloitteLogo from "@/assets/logos/deloitte.avif";
import jllLogo from "@/assets/logos/jll-transparent.png";
import cbreLogo from "@/assets/logos/cbre-transparent.png";
import winnersLogo from "@/assets/logos/winners.avif";
import fastfrateLogo from "@/assets/logos/fastfrate-transparent.png";

const logos = [
  { name: "Amazon", logo: amazonLogo },
  { name: "DHL", logo: dhlLogo },
  { name: "Coca-Cola", logo: cocaColaLogo },
  { name: "Deloitte", logo: deloitteLogo },
  { name: "JLL", logo: jllLogo },
  { name: "CBRE", logo: cbreLogo },
  { name: "Winners", logo: winnersLogo },
  { name: "Fastfrate", logo: fastfrateLogo },
];

const USAHeroSection = () => {
  // -1 = racking base image; 0..n = montage slides, deferred until idle on desktop
  const [activeSlide, setActiveSlide] = useState(-1);
  const [montageReady, setMontageReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 1024px), (prefers-reduced-motion: reduce)").matches) return;
    let cancelled = false;
    const idle: (cb: () => void) => number =
      (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback ??
      ((cb) => window.setTimeout(cb, 4000));
    const handle = idle(() => { if (!cancelled) setMontageReady(true); });
    return () => {
      cancelled = true;
      if (typeof handle === "number") {
        (window.cancelIdleCallback ?? window.clearTimeout)(handle);
      }
    };
  }, []);

  useEffect(() => {
    if (!montageReady) return;
    const interval = window.setInterval(
      // cycle -1 (racking) -> 0 -> 1 -> 2 -> back to -1
      () => setActiveSlide((prev) => (prev >= montageSlides.length - 1 ? -1 : prev + 1)),
      5000
    );
    return () => window.clearInterval(interval);
  }, [montageReady]);

  return (
    <section className="relative flex flex-col">
      {/* Full-bleed background: instant racking frame + deferred construction montage */}
      <div className="absolute inset-0">
        <style>{`
          @keyframes mtli-kenburns { from { transform: scale(1); } to { transform: scale(1.07); } }
          @keyframes mtli-sheen { 0%, 55% { background-position: 130% center; } 85%, 100% { background-position: -30% center; } }
          @keyframes mtli-drift { from { transform: translate(0, 0) scale(1); } to { transform: translate(70px, -45px) scale(1.18); } }
        `}</style>
        <picture>
          <source media="(max-width: 767px)" srcSet={rackingDramaticMobile} type="image/webp" />
          <source srcSet={rackingDramatic} type="image/webp" />
          <img
            src={rackingDramatic}
            alt="Industrial warehouse racking infrastructure"
            className="w-full h-full object-cover"
            width="848"
            height="1131"
            loading="eager"
            decoding="sync"
            {...({ fetchpriority: "high" } as any)}
          />
        </picture>
        {montageReady &&
          montageSlides.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${
                i === activeSlide ? "opacity-100" : "opacity-0"
              }`}
              style={i === activeSlide ? { animation: "mtli-kenburns 6s linear forwards" } : undefined}
            />
          ))}
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/95 via-secondary/85 to-secondary/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-transparent to-secondary/30" />
        {/* Drifting ember glow: slow-moving warmth behind the content */}
        <div
          aria-hidden="true"
          className="absolute -left-24 top-1/4 h-[440px] w-[440px] rounded-full bg-primary/25 blur-[130px] motion-reduce:hidden"
          style={{ animation: "mtli-drift 16s ease-in-out infinite alternate" }}
        />
      </div>

      {/* Main content: value prop left, callback form right */}
      <div className="relative flex-1 flex items-center">
        <div className="container mx-auto px-4 pt-14 pb-16 lg:pt-20 lg:pb-20">
          <div className="grid lg:grid-cols-[1fr_420px] gap-10 lg:gap-14 items-center">
            <div>
              <AnimatedSection animation="fade-up">
                <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-1.5 mb-6">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-primary text-sm font-medium">Serving North America Coast to Coast</span>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6">
                  One Call. One Integrated Partner.
                  <br />
                  Your Facility{" "}
                  {/* Slow light sheen sweeps the key word every few seconds */}
                  <span
                    className="text-transparent bg-clip-text motion-reduce:text-primary"
                    style={{
                      backgroundImage:
                        "linear-gradient(110deg, hsl(var(--primary)) 42%, hsl(28 100% 68%) 50%, hsl(var(--primary)) 58%)",
                      backgroundSize: "220% 100%",
                      animation: "mtli-sheen 5.5s ease-in-out infinite",
                    }}
                  >
                    Delivered.
                  </span>
                </h1>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={200}>
                <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl leading-relaxed font-light">
                  MTLI Group of Companies designs, builds, installs, and maintains
                  warehouses, distribution centers, and manufacturing facilities. Turnkey
                  delivery from under $100K to $100M+: we take on the risk, the trades, and
                  the schedule so you don&apos;t have to.
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={300}>
                <div className="space-y-3 mb-8">
                  {[
                    "One accountable team for warehouse automation, racking, construction, and electrical",
                    "A specialist calls you within 1 business day with real numbers",
                    "15,000+ projects delivered with 98% on-time completion",
                  ].map((point) => (
                    <div key={point} className="flex items-start gap-2.5 text-white/85">
                      <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-base">{point}</span>
                    </div>
                  ))}
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={400}>
                <div className="flex flex-wrap items-center gap-4">
                  <Button size="lg" variant="outline" className="text-lg px-8 py-6 bg-white/5 backdrop-blur-sm border-white/20 text-white hover:bg-white/10" asChild>
                    <a href="tel:+13054337789">
                      <Phone className="mr-2 h-5 w-5" />
                      (305) 433-7789
                    </a>
                  </Button>
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <span className="flex text-primary">
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="h-4 w-4 fill-current" />)}
                    </span>
                    Trusted by Fortune 500 operators
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Conversion form: the primary action, right in the hero */}
            <AnimatedSection animation="fade-up" delay={200}>
              <HeroCallbackForm />
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Logo marquee at bottom: slow, continuous, pauses on hover */}
      <div className="relative border-t border-white/10 bg-secondary/60 backdrop-blur-md">
        <style>{`@keyframes mtli-logos { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
        <div className="container mx-auto px-4 py-5">
          <div className="flex items-center gap-6">
            <span className="text-white/60 text-xs uppercase tracking-widest hidden md:block shrink-0">Trusted by</span>
            <div className="relative overflow-hidden flex-1">
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-secondary/80 to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-secondary/80 to-transparent z-10 pointer-events-none" />
              <div
                className="flex w-max items-center gap-10 hover:[animation-play-state:paused] motion-reduce:[animation:none]"
                style={{ animation: "mtli-logos 36s linear infinite" }}
              >
                {[...logos, ...logos].map((item, i) => (
                  <div
                    key={`${item.name}-${i}`}
                    className="flex items-center justify-center h-10 w-[120px] shrink-0 px-3 bg-white rounded-md shadow-sm"
                  >
                    <img
                      src={item.logo}
                      alt={`${item.name} logo`}
                      width={100}
                      height={28}
                      className="max-h-7 w-auto max-w-[100px] object-contain"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default USAHeroSection;
